import React from 'react';

/**
 * DataCard Component - Unified card structure for displaying data items
 * 
 * @param {string} title - Card title
 * @param {array} badges - Array of badge configs: [{ label, className, variant }]
 * @param {array} metadata - Array of metadata items: [{ label, value }]
 * @param {array} actions - Array of action configs: [{ label, onClick, variant, icon, disabled }]
 * @param {React.ReactNode} children - Custom content
 * @param {string} className - Additional CSS classes
 * @param {function} onClick - Card click handler
 */
const DataCard = ({
  title,
  badges = [],
  metadata = [],
  actions = [],
  children,
  className = '',
  onClick
}) => {
  const cardClasses = `
    p-6 rounded-lg theme-card theme-hover-lift border border-gray-200 dark:border-gray-700
    ${onClick ? 'cursor-pointer' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={cardClasses} onClick={onClick}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          {/* Title and Badges */}
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {title && (
              <h3 className="text-xl font-semibold theme-text">
                {title}
              </h3>
            )}
            {badges.map((badge, index) => (
              <span
                key={index}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  badge.className || 'bg-gray-100 text-gray-700'
                }`}
              >
                {badge.label}
              </span>
            ))}
          </div>

          {/* Metadata Grid */}
          {metadata.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              {metadata.map((item, index) => (
                <div key={index}>
                  <span className="theme-text-secondary">{item.label}:</span>
                  <span className="ml-2 theme-text font-medium">{item.value || 'N/A'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {actions.length > 0 && (
          <div className="flex gap-2 ml-4">
            {actions.map((action, index) => {
              const buttonClasses = {
                edit: 'p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors',
                delete: 'p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors',
                view: 'p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors',
                default: 'p-2 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors'
              };

              const variantClass = buttonClasses[action.variant] || buttonClasses.default;

              return (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (action.onClick && !action.disabled) {
                      action.onClick();
                    }
                  }}
                  disabled={action.disabled}
                  className={`${variantClass} ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={action.label || action.title}
                >
                  {action.icon || (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {action.variant === 'edit' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      )}
                      {action.variant === 'delete' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      )}
                      {action.variant === 'view' && (
                        <>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </>
                      )}
                      {!action.variant && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      )}
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Custom Content */}
      {children}
    </div>
  );
};

export default DataCard;

